import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const REWARD_GIFT_FLAG = '_reward_gift';

/**
 * =========================================================
 * SIMPLIFIED / RELOAD-BASED VERSION
 * =========================================================
 *
 * Instead of patching Shopify-returned section HTML in place
 * (Horizon morphing, applyCartSections, syncChain, etc.), this
 * version:
 *
 *   1. Reads the current cart.
 *   2. Adds/removes the reward gift line and syncs tier
 *      properties via plain /cart/add.js and /cart/change.js
 *      calls (no `sections` payload needed).
 *   3. If anything actually changed, does a full
 *      `window.location.reload()` so every part of the page
 *      (cart drawer, cart page, header bubble, etc.) picks up
 *      the new state naturally.
 *
 * A `_reload` global guard prevents overlapping reconciliations
 * from firing two reloads, and `addGift()` re-checks the live
 * cart for an existing gift line before adding one, so the
 * gift can only ever be added ONCE per cart.
 */

let globalListenerInstalled = false;
let reconciling = false;

function getCurrentRewardComponent() {
  return document.querySelector('cart-rewards-component');
}

/**
 * Listen globally to Horizon cart changes.
 *
 * This survives cart section morphs/replacements because it's
 * bound to `document`, not to the component instance.
 */
function installGlobalListener() {
  if (globalListenerInstalled) {
    return;
  }

  globalListenerInstalled = true;

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (!event.promise) {
      return;
    }

    event.promise
      .then(async (result) => {
        /**
         * Ignore the event this component itself would have
         * caused — irrelevant now since we reload the page
         * instead of dispatching a follow-up event, but kept
         * as a safety no-op guard in case something upstream
         * still tags a source.
         */
        if (result?.detail?.source === 'cart-rewards-component') {
          return;
        }

        const reward = getCurrentRewardComponent();

        if (!reward) {
          return;
        }

        await reward.reconcileCurrentCart();
      })
      .catch(() => {});
  });
}

export class CartRewardsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    installGlobalListener();

    /**
     * Reconcile once on load. If the page was just reloaded by
     * this component, the cart should already be in the
     * desired state, so this will be a no-op (no second
     * reload).
     */
    this.reconcileCurrentCart();
  }

  /**
   * ---------------------------------------------------------
   * SETTINGS
   * ---------------------------------------------------------
   */

  get tier2Threshold() {
    return Number(this.dataset.tier2Threshold || 0);
  }

  get tier2VariantId() {
    return this.dataset.tier2VariantId || '';
  }

  get tier3() {
    return {
      threshold: Number(this.dataset.tier3Threshold || 0),
      name: this.dataset.tier3PropertyName || '',
      value: this.dataset.tier3PropertyValue || '',
    };
  }

  get tier4() {
    return {
      threshold: Number(this.dataset.tier4Threshold || 0),
      name: this.dataset.tier4PropertyName || '',
      value: this.dataset.tier4PropertyValue || '',
    };
  }

  /**
   * ---------------------------------------------------------
   * CART HELPERS
   * ---------------------------------------------------------
   */

  async fetchCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`Cart request failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.warn('[cart-rewards] Fetch cart failed:', error);
      return null;
    }
  }

  isGift(item) {
    return item?.properties?.[REWARD_GIFT_FLAG] === 'true';
  }

  findGiftLines(cart) {
    return (cart?.items || []).filter((item) => this.isGift(item));
  }

  findMainLine(cart) {
    return (cart?.items || []).find((item) => !this.isGift(item)) || null;
  }

  getQualifyingTotal(cart) {
    return (cart?.items || []).reduce((total, item) => {
      if (this.isGift(item)) {
        return total;
      }

      return total + Number(item.final_line_price || 0);
    }, 0);
  }

  /**
   * ---------------------------------------------------------
   * RAW CART REQUESTS (no `sections` — we reload instead)
   * ---------------------------------------------------------
   */

  async changeLine(lineKey, quantity, properties) {
    const payload = { id: lineKey, quantity };

    if (properties !== undefined) {
      payload.properties = properties;
    }

    try {
      const response = await fetch(
        Theme.routes.cart_change_url,
        fetchConfig('json', { body: JSON.stringify(payload) })
      );

      const json = await response.json();

      if (!response.ok || json.errors) {
        console.warn('[cart-rewards] Cart change failed:', json.errors);
        return null;
      }

      return json;
    } catch (error) {
      console.warn('[cart-rewards] Cart change failed:', error);
      return null;
    }
  }

  /**
   * Add the reward gift line.
   *
   * IMPORTANT: re-reads the *live* cart right before adding
   * and bails out if a gift line already exists. This is what
   * guarantees the gift is only ever added ONCE per cart, even
   * if reconcileCart() is triggered again before the reload
   * lands.
   */
  async addGift() {
    if (!this.tier2VariantId) {
      return null;
    }

    const latestCart = await this.fetchCart();

    if (!latestCart) {
      return null;
    }

    const hasRealProducts = (latestCart.items || []).some(
      (item) => !this.isGift(item) && Number(item.quantity || 0) > 0
    );

    if (!hasRealProducts) {
      return null;
    }

    const existingGifts = this.findGiftLines(latestCart);

    if (existingGifts.length > 0) {
      // Gift already present — never add a second one.
      return null;
    }

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [
              {
                id: this.tier2VariantId,
                quantity: 1,
                properties: { [REWARD_GIFT_FLAG]: 'true' },
              },
            ],
          }),
        })
      );

      const json = await response.json();

      if (!response.ok || json.status || json.errors) {
        console.warn('[cart-rewards] Gift add failed:', json.errors || json.description);
        return null;
      }

      return json;
    } catch (error) {
      console.warn('[cart-rewards] Gift add failed:', error);
      return null;
    }
  }

  async removeAllGifts(cart) {
    const gifts = this.findGiftLines(cart);
    let changed = false;

    for (const gift of gifts) {
      const result = await this.changeLine(gift.key, 0);

      if (result) {
        changed = true;
      }
    }

    return changed;
  }

  /**
   * Exactly one gift line, quantity exactly 1.
   */
  async ensureSingleGift(cart) {
    let changed = false;
    const gifts = this.findGiftLines(cart);

    if (!gifts.length) {
      return changed;
    }

    if (gifts[0].quantity !== 1) {
      const result = await this.changeLine(gifts[0].key, 1);

      if (result) {
        changed = true;
      }
    }

    // Remove any duplicates beyond the first — belt and braces.
    for (let i = 1; i < gifts.length; i += 1) {
      const result = await this.changeLine(gifts[i].key, 0);

      if (result) {
        changed = true;
      }
    }

    return changed;
  }

  /**
   * ---------------------------------------------------------
   * TIER 3 / TIER 4 PROPERTIES
   * ---------------------------------------------------------
   */

  /**
   * Pick ONE deterministic line to carry the tier-3/4 reward
   * properties.
   *
   * If a product line already carries one of the tags (from an
   * earlier reconcile), keep using that same line — don't let
   * "the first non-gift line" silently shift to a newly added
   * product. Only fall back to the first product line when no
   * line has been tagged yet.
   */
  pickRewardLine(cart) {
    const mainLines = (cart?.items || []).filter((item) => !this.isGift(item));

    if (!mainLines.length) {
      return null;
    }

    const { name: tier3Name } = this.tier3;
    const { name: tier4Name } = this.tier4;

    const alreadyTagged = mainLines.find((item) => {
      const props = item.properties || {};

      return (tier3Name && props[tier3Name]) || (tier4Name && props[tier4Name]);
    });

    return alreadyTagged || mainLines[0];
  }

  /**
   * Desired tier-3/4 properties for the designated reward line.
   */
  getDesiredPropertiesForRewardLine(line, total) {
    const tier3 = this.tier3;
    const tier4 = this.tier4;
    const current = { ...(line.properties || {}) };
    const next = {};

    Object.entries(current).forEach(([key, value]) => {
      if (tier3.name && key === tier3.name) return;
      if (tier4.name && key === tier4.name) return;
      if (value === '' || value === null) return;

      next[key] = value;
    });

    if (tier3.name && tier3.threshold > 0 && total >= tier3.threshold) {
      next[tier3.name] = tier3.value;
    } else if (tier3.name && Object.prototype.hasOwnProperty.call(current, tier3.name)) {
      next[tier3.name] = '';
    }

    if (tier4.name && tier4.threshold > 0 && total >= tier4.threshold) {
      next[tier4.name] = tier4.value;
    } else if (tier4.name && Object.prototype.hasOwnProperty.call(current, tier4.name)) {
      next[tier4.name] = '';
    }

    return { current, next };
  }

  /**
   * Any tier-3/4 tags found on a line that is NOT the
   * designated reward line are stale (left over from before
   * the reward line was picked, or from a race) and must be
   * cleared so exactly one line ever shows the reward tags.
   */
  getClearedRewardProperties(line) {
    const tier3 = this.tier3;
    const tier4 = this.tier4;
    const current = { ...(line.properties || {}) };
    const next = { ...current };
    let touched = false;

    if (tier3.name && Object.prototype.hasOwnProperty.call(current, tier3.name) && current[tier3.name] !== '') {
      next[tier3.name] = '';
      touched = true;
    }

    if (tier4.name && Object.prototype.hasOwnProperty.call(current, tier4.name) && current[tier4.name] !== '') {
      next[tier4.name] = '';
      touched = true;
    }

    return touched ? { current, next } : { current, next: current };
  }

  propertiesChanged(current, next) {
    const currentKeys = Object.keys(current);
    const nextKeys = Object.keys(next);

    if (currentKeys.length !== nextKeys.length) {
      return true;
    }

    return nextKeys.some((key) => current[key] !== next[key]);
  }

  /**
   * Sync tier-3/4 properties across the WHOLE cart:
   *   - exactly one product line (the reward line) gets the
   *     desired tags for the current qualifying total
   *   - every other product line has any stray tags cleared
   *
   * This is what keeps things consistent when a second (or
   * third) product is added — the tag never jumps to or
   * duplicates onto the new line.
   */
  async syncProperties(cart) {
    const mainLines = (cart?.items || []).filter((item) => !this.isGift(item));

    if (!mainLines.length) {
      return false;
    }

    const total = this.getQualifyingTotal(cart);
    const rewardLine = this.pickRewardLine(cart);
    let changed = false;

    for (const line of mainLines) {
      const isRewardLine = rewardLine && line.key === rewardLine.key;

      const { current, next } = isRewardLine
        ? this.getDesiredPropertiesForRewardLine(line, total)
        : this.getClearedRewardProperties(line);

      if (!this.propertiesChanged(current, next)) {
        continue;
      }

      const result = await this.changeLine(line.key, line.quantity, next);

      if (result) {
        changed = true;
      }
    }

    return changed;
  }

  /**
   * ---------------------------------------------------------
   * RECONCILE
   * ---------------------------------------------------------
   */

  async reconcileCurrentCart() {
    if (reconciling) {
      return;
    }

    reconciling = true;

    try {
      const cart = await this.fetchCart();

      if (!cart) {
        return;
      }

      await this.reconcileCart(cart);
    } finally {
      reconciling = false;
    }
  }

  async reconcileCart(cart) {
    let changed = false;

    const mainLine = this.findMainLine(cart);

    /**
     * =====================================================
     * NO REAL PRODUCTS — cart is empty or only has the gift.
     * =====================================================
     */
    if (!mainLine) {
      const gifts = this.findGiftLines(cart);

      if (gifts.length > 0) {
        changed = (await this.removeAllGifts(cart)) || changed;
      }

      if (changed) {
        window.location.reload();
      }

      return;
    }

    /**
     * =====================================================
     * TIER 2 — GIFT
     * =====================================================
     */
    const qualifyingTotal = this.getQualifyingTotal(cart);
    const threshold = this.tier2Threshold;
    const giftVariant = this.tier2VariantId;

    const shouldHaveGift = threshold > 0 && !!giftVariant && qualifyingTotal >= threshold;

    const gifts = this.findGiftLines(cart);

    if (!shouldHaveGift && gifts.length > 0) {
      changed = (await this.removeAllGifts(cart)) || changed;
    } else if (shouldHaveGift && gifts.length === 0) {
      const result = await this.addGift();

      if (result) {
        changed = true;
      }
    }

    /**
     * Re-fetch if we just touched the gift line, so the
     * duplicate-check and property sync below see the real,
     * current state of the cart.
     */
    let workingCart = cart;

    if (changed) {
      const freshCart = await this.fetchCart();

      if (freshCart) {
        workingCart = freshCart;
      }
    }

    if (shouldHaveGift) {
      const normalizedChanged = await this.ensureSingleGift(workingCart);

      if (normalizedChanged) {
        changed = true;

        const freshCart = await this.fetchCart();

        if (freshCart) {
          workingCart = freshCart;
        }
      }
    }

    /**
     * =====================================================
     * TIER 3 / TIER 4 PROPERTIES
     * =====================================================
     */
    const propsChanged = await this.syncProperties(workingCart);

    if (propsChanged) {
      changed = true;
    }

    /**
     * =====================================================
     * FINAL — reload if anything actually changed.
     * =====================================================
     */
    if (changed) {
      window.location.reload();
    }
  }
}

if (!customElements.get('cart-rewards-component')) {
  customElements.define('cart-rewards-component', CartRewardsComponent);
}